import { Injectable, signal, effect, Type } from "@angular/core";

export interface ConfirmationModalButton {
  label: string;
  action: () => void | Promise<void>;
  variant?: "primary" | "secondary" | "danger" | "ghost";
  loading?: boolean;
}

export interface ConfirmationModalConfig {
  title?: string;
  message?: string;
  icon?: "success" | "error" | "warning" | "info" | "question" | "none";
  buttons?: ConfirmationModalButton[];
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  customComponent?: Type<unknown>;
  customData?: unknown;
  width?: "sm" | "md" | "lg" | "xl" | "full";
}

export interface ActiveConfirmationModal extends ConfirmationModalConfig {
  id: string;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

@Injectable({
  providedIn: "root",
})
export class ConfirmationModalService {
  private modalStack = signal<ActiveConfirmationModal[]>([]);

  public activeModals = this.modalStack.asReadonly();

  constructor() {
    // Prevent body scroll when modal is open
    effect(() => {
      const hasModals = this.modalStack().length > 0;
      if (hasModals) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    });
  }

  /**
   * Abre um modal genérico e retorna uma Promise
   */
  open<T = unknown>(config: ConfirmationModalConfig): Promise<T> {
    return new Promise((resolve, reject) => {
      const modal: ActiveConfirmationModal = {
        id: this.generateId(),
        ...config,
        resolve: resolve as (value: unknown) => void,
        reject,
      };

      this.modalStack.update((stack) => [...stack, modal]);
    });
  }

  /**
   * Confirma uma ação (sim/não)
   */
  confirm(
    message: string,
    title = "Confirmar",
    options: Partial<ConfirmationModalConfig> = {}
  ): Promise<boolean> {
    return this.open<boolean>({
      title,
      message,
      icon: "question",
      showCloseButton: true,
      closeOnBackdropClick: false,
      closeOnEscape: true,
      buttons: [
        {
          label: "Cancelar",
          variant: "secondary",
          action: () => {
            this.close(false);
          },
        },
        {
          label: "OK",
          variant: "primary",
          action: () => {
            this.close(true);
          },
        },
      ],
      ...options,
    });
  }

  /**
   * Alerta simples
   */
  alert(
    message: string,
    title = "Atenção",
    type: "success" | "error" | "warning" | "info" = "info"
  ): Promise<void> {
    return this.open<void>({
      title,
      message,
      icon: type,
      showCloseButton: true,
      closeOnBackdropClick: true,
      closeOnEscape: true,
      buttons: [
        {
          label: "OK",
          variant: "primary",
          action: () => {
            this.close();
          },
        },
      ],
    });
  }

  /**
   * Sucesso
   */
  success(message: string, title = "Sucesso"): Promise<void> {
    return this.alert(message, title, "success");
  }

  /**
   * Erro
   */
  error(message: string, title = "Erro"): Promise<void> {
    return this.alert(message, title, "error");
  }

  /**
   * Aviso
   */
  warning(message: string, title = "Aviso"): Promise<void> {
    return this.alert(message, title, "warning");
  }

  /**
   * Informação
   */
  info(message: string, title = "Informação"): Promise<void> {
    return this.alert(message, title, "info");
  }

  /**
   * Fecha o modal mais recente
   */
  close(result?: unknown): void {
    const stack = this.modalStack();
    if (stack.length === 0) return;

    const modal = stack[stack.length - 1];
    modal.resolve(result);

    this.modalStack.update((s) => s.slice(0, -1));
  }

  /**
   * Fecha um modal específico por ID
   */
  closeById(id: string, result?: unknown): void {
    const stack = this.modalStack();
    const modal = stack.find((m) => m.id === id);

    if (modal) {
      modal.resolve(result);
      this.modalStack.update((s) => s.filter((m) => m.id !== id));
    }
  }

  /**
   * Fecha todos os modais
   */
  closeAll(): void {
    const stack = this.modalStack();
    stack.forEach((modal) => modal.resolve(undefined));
    this.modalStack.set([]);
  }

  /**
   * Rejeita e fecha o modal
   */
  dismiss(reason?: unknown): void {
    const stack = this.modalStack();
    if (stack.length === 0) return;

    const modal = stack[stack.length - 1];
    modal.reject(reason);

    this.modalStack.update((s) => s.slice(0, -1));
  }

  private generateId(): string {
    return `confirmation-modal-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
}
